// Copyright (c) Docugami, Inc. All rights reserved.

import { IDocumentCommand } from "components/file_management/DocumentCommand";
import { isSupportedFile, isSupportedFilePath } from "components/file_management/file_helpers";
import { FileDropZone, useFileUploader } from "components/file_management/presentation/FileUploader";
import { UnsupportedFilesInfo, UnsupportedFilesModal } from "components/myContentExplorer/UnsupportedFilesModal";
import { ProviderPickerItem } from "components/storage/ProviderPickerItem";
import { FileUploadRequest } from "filemanager/FileUploader";
import { ProviderId, compare } from "networking/StorageProviders";
import { useMemo, useState, useEffect } from "react";
import { useDocumentUploadCommand } from "./DocumentUploadCommand";
import { useStorageProviderUploadCommand } from "./StorageProviderUploadCommand";
import { getIconForFileViewItem } from "components/file_management/presentation/DocumentViewer";
import { emptyGray } from "components/shell/presentation/AppStyles";
import { ColorIconProps } from "components/work_path/IconRenderer";
import { dgIconNames } from "./dgIcons";
import { ListItem } from "components/myContentExplorer/AddDocsListView";

interface IUploadQueueItems {
    items: ListItem[],
    onItemsDeleted: (deletedItems: ListItem[]) => void,
    onSyncChanged: (item: ListItem, isSynced: boolean) => void,
    clearState: () => void,
    overlays: JSX.Element[]
}

interface IFilePickerCommands {
    localDocsPickerCommands: IDocumentCommand[],
    providerUploadCommand: IDocumentCommand,
    dropZone: FileDropZone,
    overlays: JSX.Element[]
}

interface IFilesPicker {
    pickerCommands: IFilePickerCommands;
    queuedItemProps: IUploadQueueItems;
}

let itemsCache: ListItem[] = [];

export function useAddFilesQueue(selectedId: string | null, onComplete?: () => void, docSetId?: string): IFilesPicker {
    const selectedProviderId = useMemo(() => { return !selectedId ? null : ProviderId[selectedId as keyof typeof ProviderId] }, [selectedId]);
    const providerUploadCommand = useStorageProviderUploadCommand(selectedProviderId, () => { }, false, onProviderItemsSelected);
    const [filePicker, dropZone] = useFileUploader('', onLocalItemsSelected, () => { }, docSetId);
    const localDocsUploadCommand = useDocumentUploadCommand(filePicker, () => { });

    const commands = useMemo(() => [providerUploadCommand], [providerUploadCommand]);
    const overlays = useMemo(() => [...filePicker.inputElements, ...commands.filter(c => c.isActive && !!c.visual).map(x => x.visual) as JSX.Element[]], [commands, filePicker.inputElements]);

    const [items, setItems] = useState<ListItem[]>(itemsCache);
    const [unsupportedFiles, setUnsupportedFiles] = useState<UnsupportedFilesInfo | null>(null);

    useEffect(() => {
        if (!selectedProviderId) {
            return;
        }

        providerUploadCommand.execute();
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedProviderId]);

    function updateItems(newItems: ListItem[]) {
        setItems(newItems);
        itemsCache = [...newItems];
    }

    function onItemsDeleted(deletedItems: ListItem[]) {
        updateItems(items.filter(y => !deletedItems.includes(y)));
    }

    function onSyncChanged(item: ListItem, isSynced: boolean) {
        item.autoSync = isSynced;
        updateItems([...items]);
    }

    function onProviderItemsSelected(providerItems: ProviderPickerItem[]) {
        onAddItems({
            itemsToAdd: providerItems,
            isValid: (p) => p.isFolder || isSupportedFile(p.name),
            isEqual: (p, listItem) => isProviderItem(listItem) && compare(listItem.id.item, p.item, p.providerId),
            mapToListItem: (p) => pickerItemToListItem(p),
            getName: (p) => p.name
        })
    }

    function onLocalItemsSelected(localItems: FileUploadRequest[]) {
        onAddItems({
            itemsToAdd: localItems,
            isValid: (localItem) => isSupportedFile(localItem.file.name) && isSupportedFilePath(localItem.targetPath),
            isEqual: (localItem, listItem) => localItem.targetPath === listItem.id.targetPath,
            mapToListItem: (localItem) => fileUploadRequestToListItem(localItem),
            getName: (localItem) => localItem.file.name
        })
    }

    function onAddItems<T>(input: {
        itemsToAdd: T[],
        isValid: (item: T) => boolean,
        isEqual: (item1: T, item2: ListItem) => boolean,
        mapToListItem: (item: T) => ListItem,
        getName: (item: T) => string
    }) {
        const { itemsToAdd, isValid, isEqual, mapToListItem, getName } = input;
        if (itemsToAdd.length === 0) {
            return;
        }

        // filtering out duplicate items and unsupported file types
        const invalidItems: T[] = [];
        const filteredItemsToAdd: T[] = [];
        itemsToAdd.forEach(itemToAdd => {
            if (!isValid(itemToAdd)) {
                invalidItems.push(itemToAdd);
            }
            else if (!items.find(item => isEqual(itemToAdd, item))) {
                filteredItemsToAdd.push(itemToAdd);
            }
        })

        if (filteredItemsToAdd.length > 0) {
            updateItems([
                ...items,
                ...filteredItemsToAdd.map(mapToListItem)
            ]);
        }

        if (invalidItems.length > 0) {
            setUnsupportedFiles({
                items: invalidItems.map(getName),
                totalCount: itemsToAdd.length
            });
        }

        onComplete?.();
    }

    return {
        pickerCommands: {
            localDocsPickerCommands: localDocsUploadCommand,
            providerUploadCommand: providerUploadCommand,
            dropZone: dropZone,
            overlays: overlays
        },
        queuedItemProps: {
            items: items,
            onItemsDeleted: onItemsDeleted,
            onSyncChanged: onSyncChanged,
            clearState: () => updateItems([]),
            overlays: [<UnsupportedFilesModal key="UnsupportedFiles" files={unsupportedFiles} onDismiss={() => setUnsupportedFiles(null)} />]
        }
    }
}

export const isProviderItem = (listItem: ListItem) => !!(listItem.id as ProviderPickerItem)?.item;

const pickerItemToListItem = (item: ProviderPickerItem): ListItem => {
    return {
        id: item,
        name: item.name,
        icon: getIconForFileViewItem(item.isFolder, item.providerId),
        autoSync: item.isSynced
    }
}

const fileUploadRequestToListItem = (item: FileUploadRequest): ListItem => {
    return {
        id: item,
        name: item.file.name,
        icon: getIconForFile()
    }
}

function getIconForFile(): ColorIconProps {
    return {
        name: dgIconNames.DgFileIcon,
        width: 18,
        height: 20,
        color: emptyGray
    }
}
